import * as React from "react"
import { cn } from "../../lib/utils"

const TooltipProvider = ({ children }) => {
  return <>{children}</>
}

const TooltipContext = React.createContext({})

const Tooltip = ({ children, delayDuration = 200 }) => {
  const [open, setOpen] = React.useState(false)

  return (
    <TooltipContext.Provider value={{ open, setOpen, delayDuration }}>
      <div className="relative inline-block">
        {children}
      </div>
    </TooltipContext.Provider>
  )
}

const TooltipTrigger = React.forwardRef(({ className, children, asChild, ...props }, ref) => {
  const { setOpen, delayDuration } = React.useContext(TooltipContext)
  const timeoutRef = React.useRef(null)

  const handleMouseEnter = () => {
    timeoutRef.current = setTimeout(() => {
      setOpen(true)
    }, delayDuration)
  }

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setOpen(false)
  }

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      ref,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      ...props
    })
  }

  return (
    <div
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={className}
      {...props}
    >
      {children}
    </div>
  )
})
TooltipTrigger.displayName = "TooltipTrigger"

const TooltipContent = React.forwardRef(({ className, children, sideOffset = 4, ...props }, ref) => {
  const { open } = React.useContext(TooltipContext)
  const contentRef = React.useRef(null)
  const [position, setPosition] = React.useState('bottom')

  React.useEffect(() => {
    if (open && contentRef.current) {
      const tooltipRect = contentRef.current.getBoundingClientRect()
      const parent = contentRef.current.parentElement
      
      if (parent) {
        const parentRect = parent.getBoundingClientRect()
        
        // Find scrollable container
        let scrollContainer = parent.parentElement
        while (scrollContainer) {
          const overflow = window.getComputedStyle(scrollContainer).overflowY
          if (overflow === 'auto' || overflow === 'scroll') {
            break
          }
          scrollContainer = scrollContainer.parentElement
        }
        
        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect()
          const spaceBelow = containerRect.bottom - parentRect.bottom
          const spaceAbove = parentRect.top - containerRect.top
          
          // If not enough space below (less than 50px), show above
          if (spaceBelow < 50 && spaceAbove > 50) {
            setPosition('top')
          } else {
            setPosition('bottom')
          }
        } else {
          // Fallback to viewport check
          const viewportHeight = window.innerHeight
          if (tooltipRect.bottom > viewportHeight - 10) {
            setPosition('top')
          } else {
            setPosition('bottom')
          }
        }
      }
    }
  }, [open])

  if (!open) return null

  const isTop = position === 'top'

  return (
    <div
      ref={(node) => {
        contentRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) ref.current = node
      }}
      className={cn(
        "absolute z-[9999] overflow-hidden rounded-md bg-gray-900 px-3 py-1.5 text-xs text-white shadow-md animate-in fade-in-0 zoom-in-95",
        "left-1/2 -translate-x-1/2",
        isTop ? "bottom-full mb-2" : "top-full mt-2",
        className
      )}
      style={{ [isTop ? 'marginBottom' : 'marginTop']: `${sideOffset}px` }}
      {...props}
    >
      {children}
      <div className={cn(
        "absolute left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-transparent",
        isTop ? "top-full border-t-4 border-t-gray-900" : "bottom-full border-b-4 border-b-gray-900"
      )} />
    </div>
  )
})
TooltipContent.displayName = "TooltipContent"

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
